/**
 * The Legal holds tab of Photo Moderation (owner decision 2026-10-16, migration 370).
 *
 * Lists every legal hold with its report state, shows a hold's member data after an
 * audited view, and records the CyberTipline report id. The cards are LegalHoldCard
 * (pure). Only admins with moderation.photos.legal_hold see this tab (the page hides
 * it; the API refuses everyone else).
 */
import { useCallback, useEffect, useState } from 'react';
import { useApiUrl } from '@refinedev/core';
import { Loader2, RefreshCw, Scale } from 'lucide-react';
import {
  fetchLegalHolds,
  recordLegalHoldReport,
  viewLegalHoldSubject,
  LegalHoldActionError,
  type LegalHold,
  type LegalHoldSubject,
} from './legal-holds-api';
import { LegalHoldCard } from './LegalHoldCard';

export function LegalHoldsTab() {
  const apiUrl = useApiUrl();
  const [holds, setHolds] = useState<LegalHold[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [subjects, setSubjects] = useState<Record<string, LegalHoldSubject>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setHolds((await fetchLegalHolds(apiUrl)).data);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'The legal holds could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => { void load(); }, [load]);

  const run = useCallback(async (photoId: string, action: () => Promise<void>) => {
    setBusyId(photoId);
    setErrors((prev) => { const next = { ...prev }; delete next[photoId]; return next; });
    try {
      await action();
    } catch (e) {
      const err = e instanceof LegalHoldActionError ? e : new LegalHoldActionError(null, null, 'The request failed.');
      setErrors((prev) => ({ ...prev, [photoId]: err.message }));
    } finally {
      setBusyId(null);
    }
  }, []);

  if (loading && holds.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-3xl text-sm text-muted-foreground">
          Refused photos that a reviewer put on legal hold (possible child sexual abuse material). Report each one to the
          NCMEC CyberTipline the same day and record the report id here. The member data needed for the report is kept
          even if the member deletes the account, and is deleted 1 year after the report. Every view of it is logged.
        </p>
        <button
          type="button"
          onClick={() => void load()}
          disabled={loading}
          className="flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {loadError && (
        <div role="alert" className="rounded-lg border border-destructive bg-destructive/10 p-4 text-sm text-destructive">{loadError}</div>
      )}

      {!loadError && holds.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-lg border bg-card p-10 text-sm text-muted-foreground">
          <Scale className="h-8 w-8" />
          No legal holds.
        </div>
      ) : (
        <div className="space-y-3">
          {holds.map((hold) => (
            <LegalHoldCard
              key={hold.photo_id}
              hold={hold}
              subject={subjects[hold.photo_id] ?? null}
              busy={busyId === hold.photo_id}
              error={errors[hold.photo_id] ?? null}
              onShowSubject={(reason) => void run(hold.photo_id, async () => {
                const s = await viewLegalHoldSubject(apiUrl, hold.photo_id, reason);
                setSubjects((prev) => ({ ...prev, [hold.photo_id]: s }));
              })}
              onRecordReport={(reportId) => void run(hold.photo_id, async () => {
                const r = await recordLegalHoldReport(apiUrl, hold.photo_id, reportId);
                setHolds((prev) => prev.map((h) => (h.photo_id === hold.photo_id ? { ...h, ...r } : h)));
              })}
            />
          ))}
        </div>
      )}
    </div>
  );
}
